import {Component, computed, inject, OnInit, signal, input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {Panel} from 'primeng/panel';
import {SharedModule} from 'primeng/api';
import {ProjectStepSaveButtonComponent} from '../../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {ToastService} from '@frontend/toast';
import {
  ProjectOfficeResponse,
  SystemRecordingNonHrResponse,
  SystemRecordingsNonHrControllerService,
} from '@frontend/shared';
import {ProjectOfficesProviderService} from '../../services/project-offices-provider.service';
import {DirectusService} from '@frontend/directus';
import {rxResource} from '@angular/core/rxjs-interop';
import {firstValueFrom} from 'rxjs';
import {InputText} from 'primeng/inputtext';
import {Button} from 'primeng/button';
import {ProjectStepStateService} from '../../../../../project-step-state.service';
import {OfficeMultiSelectComponent} from '../../components/office-multi-select/office-multi-select.component';
import {ModelFieldSelectComponent} from '../../../../../../../../shared/components/inputs/model-field/model-field-select/model-field-select.component';
import {SystemRecordingStateService} from '../../system-recording-state.service';
import {LoadingComponent} from '../../../../../../../../shared/components/loading/loading.component';
import {LoadingSize} from '../../../../../../../../shared/components/loading/loading.types';
import {ConfirmationDialogService} from '../../../../../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'app-system-recording-module-non-hr',
  imports: [
    CommonModule,
    FormsModule,
    TranslocoPipe,
    Panel,
    SharedModule,
    InputText,
    Button,
    ProjectStepSaveButtonComponent,
    OfficeMultiSelectComponent,
    LoadingComponent,
    ModelFieldSelectComponent,
  ],
  templateUrl: './system-recording-module-non-hr.component.html',
})
export class SystemRecordingModuleNonHrComponent implements OnInit {
  protected readonly LoadingSize = LoadingSize;

  private readonly toastService = inject(ToastService);
  private readonly projectOfficesProviderService = inject(ProjectOfficesProviderService);
  private readonly directusService = inject(DirectusService);
  private readonly systemRecordingsNonHrController = inject(SystemRecordingsNonHrControllerService);
  private readonly stepState = inject(ProjectStepStateService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly systemRecordingStateService = inject(SystemRecordingStateService);

  readonly projectId = input.required<number>();
  readonly isReadOnly = input<boolean>(false);

  protected readonly isLoading = signal(true);
  protected readonly isSaving = signal(false);
  protected collapsed = signal(true);
  protected readonly offices = signal<ProjectOfficeResponse[]>([]);
  protected readonly entries = signal<NonHrSystemEntry[]>([]);
  protected readonly initialSnapshot = signal<NonHrSystemEntry[]>([]);

  protected readonly nonHrSystemsResource = rxResource({
    stream: () => this.directusService.loadNonHrSystems(),
  });

  protected readonly hasChanges = computed(() => {
    const currentEntries = this.entries();
    const initialEntries = this.initialSnapshot();

    if (currentEntries.length !== initialEntries.length) {
      return true;
    }

    return !this.areEntriesEqual(currentEntries, initialEntries);
  });

  protected readonly areEntriesValid = computed(() => {
    if (this.entries().length === 0) {
      return true;
    }
    return this.entries().every((entry) => this.isEntryValid(entry));
  });

  ngOnInit(): void {
    this.loadData();
  }

  protected onAddEntry(): void {
    const newEntry: NonHrSystemEntry = {
      id: crypto.randomUUID(),
      backendId: null,
      systemDirectusId: null,
      provider: '',
      tool: '',
      officeIds: [],
    };
    this.entries.set([...this.entries(), newEntry]);
  }

  protected onRemoveEntry(entryId: string): void {
    const entryToRemove = this.entries().find((entry) => entry.id === entryId);

    if (!entryToRemove) {
      return;
    }

    if (!entryToRemove.backendId) {
      this.entries.set(this.entries().filter((entry) => entry.id !== entryId));
      return;
    }

    this.confirmDialogService.confirm({
      headerKey: 'project.steps.systemRecording.confirmDeleteEntry.header',
      messageKey: 'project.steps.systemRecording.confirmDeleteEntry.message',
      accept: () => {
        this.deleteEntry(entryToRemove.backendId!);
      },
    });
  }

  private async deleteEntry(backendId: number): Promise<void> {
    this.isSaving.set(true);

    try {
      await firstValueFrom(
        this.systemRecordingsNonHrController.deleteSystemRecordingNonHr({
          projectId: this.projectId(),
          id: backendId,
        }),
      );

      const remainingEntries = this.entries().filter((entry) => entry.backendId !== backendId);

      this.entries.set(remainingEntries);
      this.initialSnapshot.set(structuredClone(this.entries()));

      await this.stepState.loadStepStatuses(this.projectId(), true);

      await this.toastService.showSuccess(
        'common.success',
        'project.steps.systemRecording.nonHrSystems.deleteSuccess',
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  protected onEntryChange(entryId: string, changes: Partial<NonHrSystemEntry>): void {
    this.entries.set(
      this.entries().map((entry) =>
        entry.id === entryId
          ? {
              ...entry,
              ...changes,
            }
          : entry,
      ),
    );
  }

  protected async loadData(showLoadingSpinner = true): Promise<void> {
    if (showLoadingSpinner) {
      this.isLoading.set(true);
    }

    try {
      const [offices, nonHrSystemRecordings] = await Promise.all([
        this.projectOfficesProviderService.getOffices(this.projectId()),
        firstValueFrom(
          this.systemRecordingsNonHrController.getAllSystemRecordingNonHr({
            projectId: this.projectId(),
          }),
        ),
      ]);

      const mappedEntries = this.mapBackendEntries(nonHrSystemRecordings.entries, offices);

      this.offices.set(offices);
      this.entries.set(mappedEntries);
      this.initialSnapshot.set(structuredClone(mappedEntries));
      this.systemRecordingStateService.nonHrAuditInfo.set(nonHrSystemRecordings.auditInfo ?? null);
    } finally {
      if (showLoadingSpinner) {
        this.isLoading.set(false);
      }
    }
  }

  protected mapBackendEntries(
    entries: SystemRecordingNonHrResponse[],
    offices: ProjectOfficeResponse[],
  ): NonHrSystemEntry[] {
    const officeMap = new Map<number, ProjectOfficeResponse>(
      offices.map((office) => [office.officeId, office]),
    );

    if (!entries || entries.length === 0) {
      return [];
    }

    return entries.map((entry) => ({
      id: crypto.randomUUID(),
      backendId: entry.id,
      systemDirectusId: entry.systemDirectusId,
      provider: entry.provider ?? '',
      tool: entry.tool ?? '',
      officeIds: entry.officeIds
        .map((officeId) => officeMap.get(officeId))
        .filter((office): office is ProjectOfficeResponse => !!office),
    }));
  }

  protected isEntryValid(entry: NonHrSystemEntry): boolean {
    return (
      !!entry.systemDirectusId &&
      entry.provider.trim().length > 0 &&
      entry.tool.trim().length > 0 &&
      entry.officeIds.length > 0
    );
  }

  protected async saveModule(): Promise<void> {
    this.isSaving.set(true);

    try {
      const validEntries = this.entries().filter(
        (entry): entry is NonHrSystemEntry & {systemDirectusId: string} => this.isEntryValid(entry),
      );

      if (validEntries.length > 0) {
        const upsertRequest = {
          entries: validEntries.map((entry) => ({
            id: entry.backendId ?? undefined,
            systemDirectusId: entry.systemDirectusId,
            provider: entry.provider.trim(),
            tool: entry.tool.trim(),
            officeIds: entry.officeIds.map((office) => office.officeId),
          })),
        };

        const response = await firstValueFrom(
          this.systemRecordingsNonHrController.upsertSystemRecordingNonHr({
            projectId: this.projectId(),
            systemRecordingNonHrUpsertRequest: upsertRequest,
          }),
        );

        const offices = this.offices();
        const savedEntries = this.mapBackendEntries(response.entries ?? [], offices);

        const savedEntriesMap = new Map(
          savedEntries.map((entry) => [
            `${entry.systemDirectusId}_${entry.provider}_${entry.tool}`,
            entry,
          ]),
        );

        const mergedEntries = this.entries().map((existingEntry) => {
          const key = `${existingEntry.systemDirectusId}_${existingEntry.provider}_${existingEntry.tool}`;
          const savedEntry = savedEntriesMap.get(key);

          if (savedEntry) {
            return savedEntry;
          }

          return existingEntry;
        });

        this.entries.set(mergedEntries);
        this.initialSnapshot.set(
          structuredClone(this.entries().filter((e) => e.backendId !== null)),
        );
      }

      await this.stepState.loadStepStatuses(this.projectId(), true);
    } finally {
      this.isSaving.set(false);
    }

    await this.toastService.showSuccess(
      'common.success',
      'project.steps.systemRecording.nonHrSystems.saveSuccess',
    );
  }

  private areEntriesEqual(
    currentEntries: NonHrSystemEntry[],
    initialEntries: NonHrSystemEntry[],
  ): boolean {
    if (currentEntries.length !== initialEntries.length) {
      return false;
    }

    for (let i = 0; i < currentEntries.length; i++) {
      const current = currentEntries[i];
      const initial = initialEntries[i];

      if (
        current.backendId !== initial.backendId ||
        current.systemDirectusId !== initial.systemDirectusId ||
        current.provider.trim() !== initial.provider.trim() ||
        current.tool.trim() !== initial.tool.trim()
      ) {
        return false;
      }

      const currentOfficeIds = current.officeIds.map((o) => o.officeId).sort((a, b) => a - b);
      const initialOfficeIds = initial.officeIds.map((o) => o.officeId).sort((a, b) => a - b);

      if (currentOfficeIds.length !== initialOfficeIds.length) {
        return false;
      }

      for (let j = 0; j < currentOfficeIds.length; j++) {
        if (currentOfficeIds[j] !== initialOfficeIds[j]) {
          return false;
        }
      }
    }

    return true;
  }
}

export interface NonHrSystemEntry {
  id: string;
  backendId: number | null;
  systemDirectusId: string | null;
  provider: string;
  tool: string;
  officeIds: ProjectOfficeResponse[];
}
